import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Container,
  AppBar,
  Toolbar,
  Divider,
  Grid,
  CircularProgress,
  Button,
} from '@mui/material';
import { CheckCircleOutline } from '@mui/icons-material';
import { getOperatorData, createCheckoutSession, createReservationAndBuildCheckoutUrl, getWaiverTemplate } from '../api';
import { loadStripe } from '@stripe/stripe-js';

const WaiverCompletePage = () => {
  const location = useLocation();  const [loading, setLoading] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [reservationHash, setReservationHash] = useState('');
  const [operatorData, setOperatorData] = useState(null);
  const [waiverTemplate, setWaiverTemplate] = useState({
    menu_logo: null,
    menu_logo_color: null,
    menu_bkg_color: null
  });
  const [userId, setUserId] = useState('');
  const [error, setError] = useState('');
  const [isFromReservation, setIsFromReservation] = useState(false);    useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const reservationHashParam = queryParams.get('reservation_hash');
    const userIdParam = queryParams.get('user_id'); // Get user_id from URL
    const operatorParam = queryParams.get('operator');
    const fleetParam = queryParams.get('fleet_id') || localStorage.getItem('fleet_id');
    
    // Check waiver source to determine if payment flow should be available
    const waiverSource = localStorage.getItem('waiverSource');
    setIsFromReservation(waiverSource === 'reservation');
    
    if (reservationHashParam) {
      setReservationHash(reservationHashParam);
    }
    
    if (userIdParam) {
      setUserId(userIdParam);
    }
      // Fetch both operator data and waiver template
    const promises = [];
    
    // Get operator param from URL or localStorage
    let finalOperatorParam = operatorParam;
    if (!finalOperatorParam) {
      const storedOperator = localStorage.getItem('operator');
      if (storedOperator) {
        try {
          const operatorObj = JSON.parse(storedOperator);
          finalOperatorParam = operatorObj.id || operatorObj.operator_id;
        } catch (e) {
          finalOperatorParam = storedOperator;
        }
      }
    }
    
    if (finalOperatorParam) {
      setLoading(true);
      
      // Fetch operator data
      const operatorPromise = getOperatorData(finalOperatorParam)
        .then((data) => {
          setOperatorData(data);
          localStorage.setItem('operator', finalOperatorParam);
        })
        .catch((err) => {
          console.error("Failed to fetch operator data:", err);
        });
      promises.push(operatorPromise);
      
      // Fetch waiver template for branding
      const templatePromise = getWaiverTemplate(finalOperatorParam, fleetParam, userIdParam)
        .then(templateData => {
          if (templateData) {
            setWaiverTemplate(templateData);
          }
        })
        .catch(err => {
          console.error('Failed to fetch waiver template:', err);
        });
      promises.push(templatePromise);
      
      // Wait for both promises to complete
      Promise.all(promises)
        .then(() => {
          setLoading(false);
          
          // Check if this is enhanced waiver flow and if we should proceed to checkout
          // Only auto-proceed if it's from reservation flow
          const reservationParamsString = localStorage.getItem('reservationParams');
          if (reservationParamsString && userIdParam && waiverSource === 'reservation') {
            proceedToCheckout();
          }
        })
        .catch(() => {
          setLoading(false);
        });
    }
  }, [location.search]);
  
  // Cleanup waiver source flag for external waivers when component unmounts
  useEffect(() => {
    return () => {
      // Only clean up if it was an external waiver (not from reservation)
      if (!isFromReservation) {
        localStorage.removeItem('waiverSource');
      }
    };
  }, [isFromReservation]);

  // Update document title with fleet account name
  useEffect(() => {
    const fleet = JSON.parse(localStorage.getItem('fleet')) || {};
    if (fleet.account_name) {
      document.title = fleet.account_name;
    } else {
      document.title = 'Roamr Reservations'; // fallback title
    }
  }, []);
  
  // Function to handle proceeding to checkout
  const proceedToCheckout = async () => {
    try {
      setRedirecting(true);
      
      // Get reservation parameters from local storage
      const reservationParamsString = localStorage.getItem('reservationParams');
      if (!reservationParamsString) {
        throw new Error('Reservation parameters not found. Please try booking again.');
      }
      
      const { params, stripe_pk, unmonetized, operator_id } = JSON.parse(reservationParamsString);

      // ACOS: unmonetized fleets skip Stripe — create the free reservation and go straight to
      // the /checkout confirmation page.
      if (unmonetized) {
        console.log('Waiver complete - unmonetized fleet, creating free reservation (no Stripe)');
        const confirmUrl = await createReservationAndBuildCheckoutUrl({ params, operator_id });
        localStorage.removeItem('reservationParams');
        window.location.href = '/' + confirmUrl;
        return;
      }

      if (!params || !stripe_pk) {
        throw new Error('Invalid reservation parameters. Please try booking again.');
      }
        console.log('Creating checkout session after waiver completion');
      console.log('Waiver complete - params being sent to createCheckoutSession:', params);
      const session = await createCheckoutSession(params);

      // Initialize Stripe and redirect to checkout
      const stripe = await loadStripe(stripe_pk);
      const { error } = await stripe.redirectToCheckout({ sessionId: session.id });

      if (error) {
        throw new Error('Failed to redirect to payment. Please try again.');
      }
    } catch (err) {
      console.error('Failed to proceed to checkout:', err);
      setError(err.message || 'Failed to process payment. Please try again.');
      setRedirecting(false);    }
  };

  return (
    <div>
      <AppBar position="static" style={{ backgroundColor: waiverTemplate.menu_bkg_color }}>
        <Toolbar>
          <Box
            component="img"
            src={waiverTemplate.menu_logo}
            alt="Waiver Logo"
            sx={{
              padding: '8px',
              borderRadius: '4px',
              height: '75px',
              objectFit: 'contain',
              // Apply color filter to make the logo the desired color
              filter: waiverTemplate.menu_logo_color?.toLowerCase() === '#ffffff' || waiverTemplate.menu_logo_color?.toLowerCase() === '#fff' 
                ? 'brightness(0) saturate(100%) invert(1)' 
                : 'none',
            }}
          />
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ mt: 4, mb: 6 }}>
        <Paper elevation={3} sx={{ p: 4 }}>          {loading || redirecting ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
              <Typography variant="body1" sx={{ mt: 2 }}>
                {redirecting ? 'Redirecting to payment...' : 'Loading...'}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center' }}>
              <CheckCircleOutline 
                sx={{ 
                  fontSize: 80, 
                  color: '#00B894', 
                  mb: 2 
                }} 
              />
              
              <Typography variant="h4" gutterBottom sx={{ color: '#00B894' }}>
                Waiver Submitted Successfully
              </Typography>
                <Typography variant="body1" paragraph>
                {userId ? 
                  (isFromReservation ? 
                    'Thank you! Your waiver has been received and processed. You can now proceed to payment.' : 
                    'Thank you! Your waiver has been received and processed.'
                  ) : 
                  `Thank you! Your waiver for reservation #${reservationHash} has been received and processed.`
                }
              </Typography>
                {error && (
                <Typography variant="body1" sx={{ color: 'error.main', mb: 2 }}>
                  {error}
                </Typography>
              )}
              
              {/* Only show payment button if waiver came from reservation flow */}
              {isFromReservation && localStorage.getItem('reservationParams') && !redirecting && (
                <Button
                  onClick={proceedToCheckout}
                  variant="contained"
                  sx={{
                    backgroundColor: '#00B894',
                    color: 'white',
                    borderRadius: '24px',
                    padding: '10px 24px',
                    fontWeight: 600,
                    mb: 4,
                    '&:hover': {
                      backgroundColor: '#009B7E',
                    },
                  }}
                >
                  Proceed to Payment
                </Button>
              )}
                <Divider sx={{ my: 4 }} />
              
              <Grid container spacing={3}>
                {operatorData && operatorData.contact_html && (
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="h6" gutterBottom>
                      Contact Information
                    </Typography>
                    <Box 
                      dangerouslySetInnerHTML={{ __html: operatorData.contact_html }} 
                      sx={{ textAlign: 'left' }}
                    />
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </Paper>
      </Container>
    </div>
  );
};

export default WaiverCompletePage;